/**
 * `SdkRuntimeContext` — per-provider runtime instances exposed to hooks.
 *
 * Holds the NON-QueryClient, NON-registry runtime objects that the
 * `<SDKProvider>` constructs and that hooks need to read:
 *
 *   - `inflightMap`: scope-keyed sentinels for in-flight mutations
 *   - `reverseIndex`: entityId → queryKey[] for O(1) NATS-event fanout
 *   - `tabNonce`: per-tab counter for cross-tab snapshot dedup
 *   - `extractIdsFromKey`: codegen-injectable id extractor; default = no ids
 *   - `dispatcher`: function called from `useNatsSubscription` per event
 *   - `persister`: long-running TanStack persister (kept for flush install)
 *
 * The QueryClient itself is provided via `@tanstack/react-query`'s own
 * `QueryClientProvider` — we don't re-expose it here.
 *
 * The `SubjectRegistry` is the `globalThis.__morfi_registry` singleton —
 * NOT a per-provider concern (see SSR-shield invariants in `server.ts`).
 */
'use client';
import { createContext, useContext } from 'react';
export const SdkRuntimeContext = createContext(null);
export function useSdkRuntime() {
    const v = useContext(SdkRuntimeContext);
    if (v === null) {
        throw new Error('useSdkRuntime: called outside <SDKProvider>. Ensure the consuming ' +
            'component is rendered inside the SDK provider tree.');
    }
    return v;
}
/** Resolve the provider-owned private-cache scope. Isolated hook tests that
 * intentionally mount only a QueryClientProvider use the anonymous scope. */
export function useSdkViewerScope() {
    return useContext(SdkRuntimeContext)?.viewerScope ?? '';
}
/** Resolve the provider-owned network scope. Isolated hook tests that mount
 * only a QueryClientProvider use an unbound scope. */
export function useSdkNetworkScope() {
    return useContext(SdkRuntimeContext)?.networkScope ?? 'unbound';
}
/**
 * The default extractor — returns `[]` for every key. Codegen overrides
 * this at SDKProvider mount via `extractIdsFromKey` prop once spec
 * emitter ships.
 */
export const defaultExtractIdsFromKey = () => [];
//# sourceMappingURL=SdkRuntimeContext.js.map